import './BookForm.scss'

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { createBookSchema } from '../../../../schemas/bookSchema'
import InputDragAndDrop from '../InputDragAndDrop/InputDragAndDrop'
import { useEffect, useState } from 'react'
import AwsService from '../../../../services/aws.service'
import BooksService from '../../../../services/books.service'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { useHistory } from 'react-router-dom'
import { UploadCoverError } from '../../../../errors/upload-cover.error'
import { DeleteCoverError } from '../../../../errors/delete-cover.error'

const BooksForm = ({ book }) => {
  const [coverImage, setCoverImage] = useState(null)

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(createBookSchema)
  })

  useEffect(() => {
    if (book) {
      const values = {
        title: book.title,
        audience: book.audience,
        language: book.language,
        numberOfChapters: book.numberOfChapters,
        description: book.description || undefined,
        readingLink: book.readingLink || undefined
      }

      reset(values)
      setCoverImage(book.cover)
    }
  }, [book, reset])

  const MySwal = withReactContent(Swal)
  const history = useHistory()

  function handleRemoveCoverPopup() {
    if (!book) {
      setCoverImage(null)
      reset({
        cover: null
      })
    } else {
      MySwal.fire({
        showCancelButton: true,
        confirmButtonColor: '#E81E47',
        cancelButtonColor: '#fff',
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
        icon: 'warning',
        title: (
          <span style={{ color: '#FFC107' }}>
            Deseja excluir a capa do livro?
          </span>
        ),
        html: (
          <p style={{ color: '#000' }}>Será ação não poderá ser revertida.</p>
        )
      }).then(async (result) => {
        if (result.isConfirmed) {
          await handleBookCover()
        } else {
          Swal.close()
        }
      })
    }
  }

  async function handleForm(data) {
    if (book) {
      const changedData = checkIfDataHasChanged(data)
      if (!changedData) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>Ok</span>,
          icon: 'info',
          title: (
            <span style={{ color: '#FFC107' }}>Nenhum campo foi editado</span>
          )
        })
      } else {
        await updateBook(changedData)
      }
    } else {
      if (data.cover === null || data.cover === undefined) {
        if (coverImage !== null) {
          data.cover = coverImage
        } else {
          delete data.cover
        }
      }
      await createBook(data)
    }
  }

  async function createBook(data) {
    try {
      const { cover, ...bookData } = data

      const bookId = await BooksService.create(bookData)

      if (cover) {
        await uploadBookCover(cover, bookId)
      }

      MySwal.fire({
        confirmButtonColor: '#87BF40',
        confirmButtonText: <span>Ok</span>,
        icon: 'success',
        title: (
          <span style={{ color: '#FFC107' }}>Livro criado com sucesso!</span>
        )
      }).then(() => {
        history.push('/books')
      })
    } catch (error) {
      if (error instanceof UploadCoverError) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>VOLTAR</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              Não foi possível fazer o upload da capa!
            </span>
          ),
          html: (
            <p style={{ color: '#000' }}>
              Tente mudar a capa novamente ao editar o livro.
            </p>
          )
        }).then(() => {
          history.push('/books')
        })
      } else {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>VOLTAR</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              Não foi possível criar um livro!
            </span>
          ),
          html: <p style={{ color: '#000' }}>{error.response.body.message}</p>
        })
      }
    }
  }

  async function updateBook(data) {
    try {
      if ('cover' in data) {
        if (book.cover.includes('amazonaws')) {
          await deleteBookCover(book.id)
        }

        if (data.cover instanceof File) {
          await uploadBookCover(data.cover, book.id)
          delete data.cover
        }
      }

      if (Object.entries(data).length === 0) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>Ok</span>,
          icon: 'success',
          title: (
            <span style={{ color: '#FFC107' }}>
              Livro atualizado com sucesso!
            </span>
          )
        }).then(() => {
          history.push('/books')
        })
      } else {
        await BooksService.update(book.id, data)

        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>Ok</span>,
          icon: 'success',
          title: (
            <span style={{ color: '#FFC107' }}>
              Livro atualizado com sucesso!
            </span>
          )
        }).then(() => {
          history.push('/books')
        })
      }
    } catch (error) {
      if (error instanceof DeleteCoverError) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>VOLTAR</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              Não foi possível deletar a capa anterior!
            </span>
          ),
          html: (
            <p style={{ color: '#000' }}>
              Aguarde alguns instantes e tente novamente.
            </p>
          )
        })
      }

      if (error instanceof UploadCoverError) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>VOLTAR</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              Não foi possível fazer o upload da capa!
            </span>
          ),
          html: (
            <p style={{ color: '#000' }}>
              Tente mudar a capa novamente ao editar o livro.
            </p>
          )
        })
      }
    }
  }

  async function uploadBookCover(cover, bookId) {
    try {
      const aws = new AwsService()

      const fileName = `capa_livro.${cover.name.split('.')[1]}`
      await aws.uploadfile(cover, fileName, bookId)

      const body = {
        cover: `https://ifa-app.s3.us-east-2.amazonaws.com/${bookId}/capa_livro.${
          cover.name.split('.')[1]
        }`
      }

      await BooksService.update(bookId, body)
    } catch (error) {
      throw new UploadCoverError()
    }
  }

  async function deleteBookCover(bookId) {
    try {
      const aws = new AwsService()
      const bookCover = await aws.getFiles(bookId)
      return await aws.deleteFile(bookCover[0].Key)
    } catch (error) {
      throw new DeleteCoverError()
    }
  }

  async function handleBookCover() {
    try {
      if (book.cover.includes('amazonaws')) {
        await deleteBookCover(book.id)

        const updateBookBody = {
          cover: 'https://placehold.co/600x900?text=Livro+sem+capa&font=poppins'
        }

        await BooksService.update(book.id, updateBookBody)

        setCoverImage(
          'https://placehold.co/600x900?text=Livro+sem+capa&font=poppins'
        )
      } else {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>Ok</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              O livro já não possui capa!
            </span>
          )
        })
      }
    } catch (error) {
      if (error instanceof DeleteCoverError) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>VOLTAR</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              Não foi possível deletar a capa!
            </span>
          ),
          html: (
            <p style={{ color: '#000' }}>
              Aguarde alguns instantes e tente novamente.
            </p>
          )
        })
      } else {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>VOLTAR</span>,
          icon: 'error',
          title: (
            <span style={{ color: '#FFC107' }}>
              Não foi possível atualizar o livro sem capa!
            </span>
          ),
          html: (
            <p style={{ color: '#000' }}>
              Tente mudar a capa novamente ao editar o livro.
            </p>
          )
        })
      }
    }
  }

  function checkIfDataHasChanged(formData) {
    const changedFields = {}
    let hasChanges = false

    Object.keys(formData).forEach((key) => {
      if (key === 'cover') {
        if (coverImage !== book[key]) {
          changedFields[key] = coverImage
          hasChanges = true
        }
      } else {
        if (formData[key] !== book[key]) {
          changedFields[key] = formData[key]
          hasChanges = true
        }
      }
    })

    return !hasChanges ? null : changedFields
  }

  return (
    <form
      className='create-book-form'
      id={book ? 'edit-book-form' : 'create-book-form'}
      onSubmit={handleSubmit(handleForm)}
    >
      <div className='drag-and-drop-container'>
        <InputDragAndDrop
          setCoverImage={setCoverImage}
          coverImage={coverImage}
        />
        <button
          className='button-full remove-image-button'
          type='button'
          onClick={handleRemoveCoverPopup}
        >
          {book ? 'Remover capa' : 'Não adicionar capa'}
        </button>
      </div>

      <div className='book-form-fields'>
        <div className='form-group'>
          <label>
            Título:<span className='highlighter'>*</span>
          </label>
          <input
            type='text'
            {...register('title')}
            className='form-control'
          />
        </div>

        <div className='form-group book-create-cover-form-container'>
          <label>Capa:</label>
          <div className='cover-input-container'>
            <input
              type='file'
              className='form-control'
              {...register('cover')}
              onChange={(e) => setCoverImage(e.target.files[0])}
            />

            <div className='cover-actions-buttons'>
              {book && (
                <a
                  href={book.cover}
                  className='button-full see-image-button'
                  target='_blank'
                  rel='noreferrer'
                >
                  <i
                    className='fa fa-link'
                    aria-hidden='true'
                  ></i>
                </a>
              )}

              <button
                className='button-full remove-image-button'
                type='button'
                onClick={handleRemoveCoverPopup}
              >
                <i
                  className='fa fa-trash'
                  aria-hidden='true'
                />
              </button>
            </div>
          </div>
        </div>

        <div className='form-group'>
          <label>
            Público:<span className='highlighter'>*</span>
          </label>
          <select
            {...register('audience')}
            className='form-control'
          >
            <option
              hidden
              value=''
            >
              Selecione uma opção
            </option>
            <option value='Instructor'>Instrutor</option>
            <option value='Participant'>Participante</option>
            <option value='Public'>Ambos</option>
          </select>
        </div>

        <div className='form-group'>
          <label>
            Idioma:<span className='highlighter'>*</span>
          </label>
          <select
            {...register('language')}
            className='form-control'
          >
            <option
              hidden
              value=''
            >
              Selecione uma opção
            </option>
            <option value='PT'>Português</option>
            <option value='EN'>Inglês</option>
            <option value='ES'>Espanhol</option>
          </select>
        </div>

        <div className='form-group'>
          <label>
            Número de capítulos:<span className='highlighter'>*</span>
          </label>
          <input
            {...register('numberOfChapters')}
            type='number'
            className='form-control'
          />
        </div>

        <div className='form-group'>
          <label>Descrição/ Sinopse:</label>
          <textarea
            {...register('description')}
            className='form-control'
            rows={4}
          />
        </div>

        <div className='form-group'>
          <label>Link de leitura:</label>
          <input
            type='text'
            {...register('readingLink')}
            className='form-control'
          />
          {errors.readingLink && <span>{errors.readingLink.message}</span>}
        </div>
      </div>
    </form>
  )
}

export default BooksForm
